import { useState, type FC } from 'react'

import type { WhiteLabel } from '@skytech/types'
import { LoaderWrapper, PageLoader, Select, StyledText } from '@skytech/ui'
import { Container } from '@skytech/ui/Container'
import { getWhiteLabelForDealerStyle } from '@skytech/utils'
import { useQuery } from '@tanstack/react-query'
import { twJoin } from 'tailwind-merge'

import { productsResources } from '@/api/products'
import queries from '@/api/queries'
import { useAppContext } from '@/App.context'
import Page from '@/components/content/Page'
import { useTranslation } from '@/components/i18n/hooks'
import { Translation } from '@/components/i18n/Translation'

import { MainProducts } from './MainProducts'
import { RelatedProducts } from './RelatedProducts'

import './style.scss'

export const styleBorderByWhiteLabel: Partial<Record<WhiteLabel, string>> = {
  skytech: 'border-skytech-orange/40 ',
  mobit: 'border-mobit-darkBlue/70',
  elkjop: 'border-elkjop/70',
  arp: 'border-arp/70',
  csi: 'border-csi-maroon/70',
}

export const ProductsPage: FC = () => {
  const { whiteLabel } = useAppContext()
  const translate = useTranslation()

  const { data, isLoading } = useQuery(
    queries.productsClassifications(),
    productsResources.getClassifications
  )
  const classifications = data?.classifications || []

  if (!classifications) {
    return <PageLoader whiteLabel={getWhiteLabelForDealerStyle(whiteLabel)} />
  }
  const [selectedClassification, setSelectedClassification] = useState(classifications[0]) // Default: ALL
  const [selectedProductId, setSelectedProductId] = useState<number | undefined>()
  const [numberOfRelated, setNumberOfRelated] = useState<number | undefined>()

  return (
    <Page className="products-page overflow-hidden">
      <h1>
        <Translation id="Products" />
      </h1>
      <LoaderWrapper isLoading={isLoading}>
        {classifications.length > 0 && (
          <Select
            id="classification"
            className="w-full md:w-2/6"
            value={selectedClassification}
            options={classifications.map(item => ({
              value: item,
              label: translate(`classifications/${item}`),
            }))}
            defaultValue={{
              value: classifications[0],
              label: translate(`classifications/${classifications[0]}`),
            }}
            onChange={classification => {
              if (classification) {
                setSelectedClassification(classification.value)
                setSelectedProductId(undefined)
              }
            }}
          />
        )}

        {/** ProductTables */}
        <div className="mt-4 flex max-w-full flex-col gap-8 md:h-[55rem] md:flex-row md:gap-4">
          <div className="basis-1/2">
            <MainProducts
              selectedCategory={selectedClassification}
              selectedProductId={selectedProductId}
              numberOfRelated={numberOfRelated}
              onProductSelect={id => {
                setSelectedProductId(selectedProductId === id ? undefined : id)
              }}
            />
          </div>
          <div className="basis-1/2">
            {selectedProductId ? (
              <RelatedProducts
                selectedProductId={selectedProductId}
                setNumberOfRelated={value => setNumberOfRelated(value)}
              />
            ) : (
              <Container
                className={twJoin(
                  'mx-0 rounded-lg border-2 border-solid pb-4 text-center shadow-lg md:h-[55rem] md:pb-0',
                  styleBorderByWhiteLabel[whiteLabel] || 'border-skytech-orange/40'
                )}
              >
                <StyledText colorMode="black" size="large">
                  <Translation id="Select a product to view related items" />
                </StyledText>
              </Container>
            )}
          </div>
        </div>
      </LoaderWrapper>
    </Page>
  )
}
